import { AUTH_ENDPOINTS } from "../../auth/endpoints";
import { baseFetch, ApiClientError } from "../baseFetch";
import { getDeviceInfo } from "../../../utils/deviceInfo";
import { showToast } from "../../../utils/showToast";
import { LocalizationKeys } from "../../../i18n";
import { ToastType } from "../../../types/toast";

/**
 * Менеджер для обработки refresh токена и очереди запросов
 * Гарантирует, что только один refresh выполняется одновременно
 */
class AuthInterceptor {
  private isRefreshing = false;
  private refreshPromise: Promise<void> | null = null;
  private pendingRequests: Array<{
    resolve: () => void;
    reject: (error: Error) => void;
  }> = [];

  /**
   * Обработчик при неудачном refresh (401 на refresh endpoint)
   * Устанавливается извне (из UserProvider)
   */
  private onAuthFailedCallback?: () => void;

  /**
   * Устанавливает callback для обработки полной потери авторизации
   */
  setOnAuthFailed(callback: () => void): void {
    this.onAuthFailedCallback = callback;
  }

  /**
   * Очищает очередь ожидающих запросов
   */
  private resolvePendingRequests(): void {
    this.pendingRequests.forEach(({ resolve }) => resolve());
    this.pendingRequests = [];
  }

  /**
   * Отклоняет все ожидающие запросы с ошибкой
   */
  private rejectPendingRequests(error: Error): void {
    this.pendingRequests.forEach(({ reject }) => reject(error));
    this.pendingRequests = [];
  }

  /**
   * Выполняет refresh токена
   * Если refresh уже идёт - возвращает существующий промис
   */
  async performRefresh(): Promise<void> {
    // Если уже идёт refresh - возвращаем существующий промис
    if (this.isRefreshing && this.refreshPromise) {
      return this.refreshPromise;
    }

    this.isRefreshing = true;

    this.refreshPromise = (async () => {
      try {
        // Используем baseFetch для единообразной обработки ошибок
        await baseFetch<void>(AUTH_ENDPOINTS.refresh, {
          method: "POST",
          body: JSON.stringify({
            deviceInfo: getDeviceInfo(),
          }),
        });

        // Уведомляем все ожидающие запросы об успехе
        this.resolvePendingRequests();
      } catch (error) {
        // Refresh не удался - токены невалидны
        const errorObj =
          error instanceof Error ? error : new Error("Token refresh failed");

        // Уведомляем все ожидающие запросы об ошибке
        this.rejectPendingRequests(errorObj);

        // Показываем toast уведомление об ошибке авторизации
        // NOTE: This is the only place where 401 errors show a toast
        // Regular 401 errors don't show toast (suppressed in baseFetch)
        showToast(LocalizationKeys.TOAST_AUTH_SESSION_EXPIRED, ToastType.Error);

        // Вызываем callback для разлогинивания
        this.onAuthFailedCallback?.();

        throw errorObj;
      } finally {
        this.isRefreshing = false;
        this.refreshPromise = null;
      }
    })();

    return this.refreshPromise;
  }

  /**
   * Добавляет запрос в очередь ожидания refresh
   */
  waitForRefresh(): Promise<void> {
    return new Promise((resolve, reject) => {
      this.pendingRequests.push({ resolve, reject });
    });
  }

  /**
   * Проверяет, идёт ли сейчас refresh
   */
  isRefreshInProgress(): boolean {
    return this.isRefreshing;
  }
}

// Singleton instance
export const authInterceptor = new AuthInterceptor();

/**
 * Проверяет, является ли ошибка 401 Unauthorized
 */
function isUnauthorizedError(error: unknown): boolean {
  return error instanceof ApiClientError && error.status === 401;
}

/**
 * API клиент с автоматической обработкой авторизации
 *
 * Выполняет запрос с автоматическим refresh токена при получении 401 ошибки
 * Используется только для запросов, требующих авторизации
 *
 * @param endpoint - путь к endpoint
 * @param init - параметры запроса
 * @returns Результат запроса
 */
export async function authenticatedApiClient<T>(
  endpoint: string,
  init: RequestInit = {},
): Promise<T> {
  // Создаём функцию для выполнения запроса
  const makeRequest = () => baseFetch<T>(endpoint, init);

  try {
    // Выполняем запрос
    return await makeRequest();
  } catch (error) {
    // Если не 401 - пробрасываем ошибку дальше
    if (!isUnauthorizedError(error)) {
      throw error;
    }

    // Получили 401 - пытаемся обновить токен
    try {
      if (authInterceptor.isRefreshInProgress()) {
        // Refresh уже идёт - ждём его завершения
        await authInterceptor.waitForRefresh();
      } else {
        // Запускаем новый refresh
        await authInterceptor.performRefresh();
      }

      // Refresh успешен - повторяем оригинальный запрос
      return await makeRequest();
    } catch {
      // Refresh не удался - ошибка уже обработана в performRefresh
      // Пробрасываем оригинальную ошибку
      throw error;
    }
  }
}
